import { autorun, computed, makeObservable, observable, runInAction } from 'mobx'
import i18n, { changeLanguage } from 'i18next'

import {
    Tenant,
    TenantAccess,
    TenantPermissionScope,
    TenantPermission,
    User,
    Language,
    availableLanguages,
} from '@authy/lib-common/entities'
import { hasTenantPermissions } from '@authy/lib-common/utils/tenant'
import { ObjectId } from '@authy/lib-common/utils/objectid'
import { changeI18nErrorMap } from '@authy/lib-common/utils/zod'

import { initAuth, Auth, AuthSettings } from '@/auth'
import { loadDebugOptions } from '@/app/AppDebugBar'
import { env } from '@/env'

import type { setUser, setTag, addBreadcrumb, getCurrentScope, captureException } from '@sentry/core'

type SentryCoreType = {
    setUser: typeof setUser
    setTag: typeof setTag
    addBreadcrumb: typeof addBreadcrumb
    getCurrentScope: typeof getCurrentScope
    captureException: typeof captureException
}

export type ClientAppStateInitParams = {
    forceUser: boolean
    authSettings?: Partial<AuthSettings>
}

export type BaseTenant = Pick<Tenant, '_id' | 'shortId' | 'country' | 'name'>

const defaultLanguage = 'de'

export class ClientAppState<TTenant extends BaseTenant = BaseTenant> {
    language: Language = defaultLanguage
    auth!: Auth
    // @ts-expect-error will be assign on init
    user: User
    tenant: TTenant | null = null
    sentry: SentryCoreType | null = null

    constructor() {
        const browserLanguages = window.navigator.languages.map((locale) => (locale || '').split('-')[0])
        const browserLanguage = browserLanguages.find((lang) => availableLanguages.includes(lang as Language)) || null
        this.language = (browserLanguage as null | Language) || defaultLanguage

        makeObservable(this, {
            language: observable,
            auth: observable.ref,
            user: observable,
            tenant: observable,
            tenantAccess: computed,
        })
    }

    async loadUser(forceLoad: boolean = false) {
        await this.auth.loadCreateUser(forceLoad, (user) =>
            runInAction(() => {
                if (user) {
                    this.user = user
                    if (this.user) {
                        this.language = this.user.language ?? this.language
                        this.sentry?.setUser({ id: this.user.userId, email: this.user.email })
                    }
                }
            })
        )
    }

    setTenant(tenant: TTenant | null) {
        runInAction(() => {
            this.tenant = tenant
            this.sentry?.setTag('tenant', this.tenant?.shortId)
        })
    }

    async initSentry(sentry?: SentryCoreType) {
        if (sentry) {
            this.sentry = sentry

            const sentryOptions = sentry.getCurrentScope().getClient()?.getOptions()
            if (sentryOptions) {
                sentryOptions.beforeBreadcrumb = (breadcrumb) => {
                    // ignore fetch breadcrumbs to the api, because we already are adding them manually
                    if (
                        breadcrumb.type === 'http' &&
                        breadcrumb.category === 'fetch' &&
                        breadcrumb.data &&
                        typeof breadcrumb.data.url === 'string' &&
                        breadcrumb.data.url.startsWith(`${env.VITE_PRODUCT_ENDPOINT}/trpc`)
                    ) {
                        return null
                    }

                    return breadcrumb
                }
            }

            if (this.auth.userId) {
                this.sentry?.setUser({ id: this.auth.userId })
            }
        }
    }

    async init({ forceUser, authSettings = {} }: ClientAppStateInitParams) {
        //
        // Init auth
        //
        this.auth = await initAuth({
            issuer: env.VITE_AUTH_PROVIDER_ISSUER,
            clientId: env.VITE_AUTH_PROVIDER_CLIENT_ID,
            forceUser,
            ...authSettings,
        })

        if (this.auth.userId) {
            await this.loadUser()
        }

        //
        // Autorun this whenver language or country changes
        //

        autorun(() => {
            const country = this.tenant?.country
            const locale = country ? `${this.language}-${country}` : this.language
            changeLanguage(locale)
            document.documentElement.lang = this.language
            changeI18nErrorMap(i18n)
        })
    }

    get isLocalhost() {
        return import.meta.env.DEV
    }

    get isDevelopment() {
        return env.VITE_PRODUCT_ENV !== 'production'
    }

    get showUpcomingFeatures() {
        return env.VITE_PRODUCT_ENV !== 'production' && !loadDebugOptions().hideUpcomingFeatures
    }

    get tenantAccess(): TenantAccess | null {
        if (!this.user || !this.tenant) {
            return null
        }

        const tenantId = this.tenant._id

        return this.user.tenants.find(({ tenant: t }) => t.equals(tenantId)) || null
    }

    hasTenantPermissions(
        permissions: Partial<Record<TenantPermissionScope, Partial<TenantPermission>>>,
        accessObject: ObjectId | false | undefined = undefined
    ) {
        return hasTenantPermissions(this.tenantAccess, permissions, accessObject)
    }
}
